import axios from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import { CamelCaseCodeInput } from 'components/final-form/Inputs';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { getAllLabTestComponents } from 'services/labTestComponentService';
import { addLabTestPanel, editLabTestPanel } from 'services/labTestPanelService';
import styled from 'styled-components';
import { LabTestComponentDto, UpsertLabTestPanelDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';


const UpsertLabTestComponentFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface UpsertLabTestPanelFormDto extends UpsertLabTestPanelDto {
  componentId?: number;
}

interface Props extends MessageSourceProps {
  labTestPanelId?: number,
  labTestPanel?: UpsertLabTestPanelFormDto,
  onCancel: () => void,
  onSave: () => void
}

const cancelTokenSource = axios.CancelToken.source();

const UpsertLabTesPanelForm = (props: Props) => {

  const {labTestPanelId, labTestPanel, onCancel, onSave, getMessage} = props;

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [labTestComponents, setLabTestComponents] = useState<LabTestComponentDto[]>([]);

  const [pageDataLoaded, setPageDataLoaded] = useState(false);

  const labTestComponentOptions = mapToDropdownOptionArray(
      labTestComponents, "name", "id", "id");

  const initialValues = useRef<Partial<UpsertLabTestPanelFormDto>>();

  useEffect(() => {

    if (labTestPanel) {
      labTestPanel.componentId = labTestPanel.simplePanel ? labTestPanel.componentIds[0] : undefined;
      initialValues.current = {...labTestPanel};
    } else {
      initialValues.current = {simplePanel: false};
    }

    fetchData();
  }, [])

  const fetchData = async () => {
    const labTestComponentList = await getAllLabTestComponents(cancelTokenSource);
    setLabTestComponents(labTestComponentList);
    setPageDataLoaded(true);
  }

  const handleSubmit = async (values: UpsertLabTestPanelFormDto) => {

    let saveValues: UpsertLabTestPanelDto = {
      ...values,
      componentIds: values.simplePanel ? [values.componentId!] : values.componentIds
    }


    const handleError = () => {
      setErrorMessage(getMessage("error.general"));
    };

    if (labTestPanelId && labTestPanel) {

      await editLabTestPanel(labTestPanelId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError);
    } else {
      await addLabTestPanel(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError);
    }

  }

  const renderFinalForm = (): React.ReactNode => {

    return (
        <FinalForm
            onSubmit={(values: any) => handleSubmit(values)}
            initialValues={initialValues.current}
            subscription={{values: true, pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  }

  const renderForm = ({values, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertLabTestComponentFormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestPanel.code")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="code"
                        component={CamelCaseCodeInput}
                        validate={required}
                        disabled={labTestPanelId !== undefined}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestPanel.name")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="name"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestPanel.simplePanel")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="simplePanel"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestPanel.components")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    {
                      values.simplePanel
                          ? <Field
                              fluid
                              name="componentId"
                              component={Select}
                              options={labTestComponentOptions}
                              validate={required}
                          />
                          : <Field
                              fluid
                              name="componentIds"
                              component={Select}
                              multiple
                              options={labTestComponentOptions}
                              validate={required}
                          />
                    }
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpsertLabTestComponentFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  return (
      <>
        {pageDataLoaded ? renderFinalForm() : <></>}
      </>
  )
}

export default withMessages(UpsertLabTesPanelForm);
